import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '@common/components/Logo'
import LogoWithLink from '@common/components/Logo/LogoWithLink'

interface ErrorConfig {
    title: string
    description: string
    showRetry: boolean
    actionText?: string
    actionLink?: string
}

const errorConfigs: Record<string, ErrorConfig> = {
    otp_expired: {
        title: 'Link Expired',
        description: 'The password reset link has expired. Please request a new one.',
        showRetry: false
    },
    access_denied: {
        title: 'Access Denied',
        description: 'You do not have permission to access this resource.',
        showRetry: false
    },
    email_not_confirmed: {
        title: 'Email Not Verified',
        description: 'Please verify your email address before continuing.',
        showRetry: true,
        actionText: 'Resend Verification Email',
        actionLink: '/auth?mode=resend-verification'
    },
    invalid_credentials: {
        title: 'Invalid Credentials',
        description: 'The email or password you entered is incorrect.',
        showRetry: true,
        actionText: 'Try Again',
        actionLink: '/auth'
    },
    default: {
        title: 'An Error Occurred',
        description: 'Something went wrong. Please try again later.',
        showRetry: true,
        actionText: 'Go Back',
        actionLink: '/auth'
    }
}

export default function ErrorPage() {
    const navigate = useNavigate()
    const [errorConfig, setErrorConfig] = useState<ErrorConfig>(errorConfigs.default)
    const [errorDescription, setErrorDescription] = useState('')

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const errorCode = searchParams.get('error_code')
        const errorDesc = searchParams.get('error_description')
        const error = searchParams.get('error')

        // 获取错误配置
        const config = errorCode && errorConfigs[errorCode]
            ? errorConfigs[errorCode]
            : errorConfigs.default

        setErrorConfig(config)

        // 如果有具体的错误描述，使用它
        if (errorDesc) {
            setErrorDescription(decodeURIComponent(errorDesc))
        } else if (error) {
            setErrorDescription(decodeURIComponent(error))
        }

        // 清除 URL 中的错误参数
        if (errorCode || errorDesc || error) {
            const cleanUrl = window.location.pathname
            window.history.replaceState({}, '', cleanUrl)
        }
    }, [])

    const handleAction = () => {
        if (errorConfig.actionLink) {
            navigate(errorConfig.actionLink)
        }
    }

    const handleGoBack = () => {
        navigate('/auth')
    }

    return (
        <div className="flex flex-col justify-between items-center w-full h-screen bg-white">
            {/* Logo */}
            <div className="fixed top-8 left-8">
                <LogoWithLink size="26px" />
            </div>

            {/* Error Content */}
            <div className="flex items-center justify-center w-full h-full">
                <div className="flex flex-col items-center gap-6 p-12 text-center max-w-md">
                    {/* Error Icon */}
                    <div className="text-red-500 mb-2">
                        <svg
                            width="80"
                            height="80"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                            <path
                                d="M12 8V12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                            <circle cx="12" cy="16" r="1" fill="currentColor" />
                        </svg>
                    </div>

                    {/* Error Title */}
                    <h2 className="text-2xl font-semibold text-gray-900 m-0">
                        {errorConfig.title}
                    </h2>

                    {/* Error Description */}
                    <p className="text-base text-gray-600 m-0">
                        {errorDescription || errorConfig.description}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3 w-full max-w-xs mt-2">
                        {errorConfig.showRetry && errorConfig.actionText && (
                            <button
                                onClick={handleAction}
                                className="w-full h-11 px-6 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-700 transition-colors"
                            >
                                {errorConfig.actionText}
                            </button>
                        )}

                        <button
                            onClick={handleGoBack}
                            className="w-full h-11 px-6 bg-transparent text-gray-900 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                        >
                            Back to Sign In
                        </button>
                    </div>

                    {/* Help Text */}
                    <div className="mt-4 pt-6 border-t border-gray-200 w-full max-w-md">
                        <p className="text-sm text-gray-400 m-0">
                            Need help? Contact our support team
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
